/**
 * Iter 60 — Mobile Labour Registration with 3 tabs.
 *
 *   1. Add One     — single-labour form (Name, Aadhar, Mobile, DOB, Project all mandatory).
 *                    On save → auto-navigates to the face-enrol kiosk for that labour.
 *   2. Upload List — Excel bulk import.
 *   3. List        — Searchable list of all labour, with a "Enrol Face" CTA next to
 *                    each entry that hasn't been face-enrolled yet.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Upload, Download, Loader2, CheckCircle2, AlertTriangle, Camera, Search, ChevronRight, Zap, ArrowRightLeft, Building2, X, Filter, FileText, Trash2 } from 'lucide-react';
import axios from 'axios';
import { apiGet, apiPost, apiDel, API } from '../../lib/api';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const initialForm = {
  name: '', aadhar_number: '', mobile: '', dob: '', project_id: '',
  gender: '', trade: '', designation: '', daily_wage: '', address: '',
  subcontractor_id: '', supervisor_name: '',
};

export default function MobileLabourRegister() {
  const navigate = useNavigate();
  const [tab, setTab] = useUrlTab('tab', 'add');
  const [projects, setProjects] = useState([]);
  const [projectNames, setProjectNames] = useState([]); // Item 2 — unscoped org-wide names, Transfer-to picker only
  const [masters, setMasters] = useState({});
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [labourList, setLabourList] = useState([]);
  const [listQuery, setListQuery] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [listFilters, setListFilters] = useState({ project_id: '', subcontractor_id: '', trade: '' });
  const [showListFilters, setShowListFilters] = useState(false);
  // Transfer state — shared between Transfer Site and Transfer Subcontractor (Item 1)
  const [transferring, setTransferring] = useState(null);
  const [transferKind, setTransferKind] = useState('site'); // 'site' | 'subcontractor'
  const [transferToId, setTransferToId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [newSubName, setNewSubName] = useState('');
  const [submittingSub, setSubmittingSub] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  // G7 (30-Sep-2026 Chunk C): Aadhaar/PAN/etc document upload, view, replace, verify
  const [docsFor, setDocsFor] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docTypeToUpload, setDocTypeToUpload] = useState('Aadhaar');
  const [docUploading, setDocUploading] = useState(false);
  const LABOUR_DOC_TYPES = ['Aadhaar', 'PAN', 'Bank Passbook', 'Photo', 'Other'];
  const loadDocs = async (labourId) => {
    setDocsLoading(true);
    try { const r = await apiGet(`/labour-master/${labourId}/documents`); setDocs(r.documents || []); }
    catch { toast.error('Failed to load documents'); }
    finally { setDocsLoading(false); }
  };
  const openDocs = (l) => { setDocsFor(l); loadDocs(l.id); };
  const closeDocs = () => { setDocsFor(null); setDocs([]); };
  const uploadDoc = async (file, docType, replaceId) => {
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/labour-master/${docsFor.id}/documents`, fd, { headers: { Authorization: `Bearer ${token}` } });
      if (replaceId) await apiDel(`/labour-master/${docsFor.id}/documents/${replaceId}`);
      toast.success(replaceId ? `${docType} replaced` : `${docType} uploaded`);
      loadDocs(docsFor.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setDocUploading(false); }
  };
  const verifyDoc = async (docId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/labour-master/${docsFor.id}/documents/${docId}/verify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Marked verified'); loadDocs(docsFor.id);
    } catch { toast.error('Verify failed'); }
  };
  const deleteDoc = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try { await apiDel(`/labour-master/${docsFor.id}/documents/${docId}`); toast.success('Removed'); loadDocs(docsFor.id); }
    catch { toast.error('Failed'); }
  };
  const fileRef = useRef(null);

  const loadSubs = () => {
    apiGet('/labour/subcontractors').then((r) => setSubcontractors(r?.items || [])).catch(() => {});
  };

  useEffect(() => {
    apiGet('/projects').then((r) => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
    apiGet('/projects/names').then((r) => setProjectNames(r?.items || [])).catch(() => {});
    apiGet('/config-masters').then((r) => setMasters(r.masters || {})).catch(() => {});
    loadSubs();
  }, []);

  const loadList = () => {
    setListLoading(true);
    const params = new URLSearchParams();
    if (listFilters.project_id) params.set('project_id', listFilters.project_id);
    if (listFilters.subcontractor_id) params.set('subcontractor_id', listFilters.subcontractor_id);
    if (listFilters.trade) params.set('trade', listFilters.trade);
    apiGet(`/labour-master${params.toString() ? '?' + params.toString() : ''}`).then((r) => {
      setLabourList(Array.isArray(r) ? r : (r?.items || r?.labour || []));
    }).catch(() => toast.error('Could not load labour list')).finally(() => setListLoading(false));
  };
  useEffect(() => { if (tab === 'list') loadList(); }, [tab, listFilters]); // eslint-disable-line

  const openTransfer = (l, kind) => {
    setTransferring(l);
    setTransferKind(kind);
    setTransferToId('');
    setTransferReason('');
  };
  const closeTransfer = () => { setTransferring(null); setTransferToId(''); setTransferReason(''); };

  const doTransfer = async () => {
    if (!transferToId) { toast.error(transferKind === 'site' ? 'Select a destination site' : 'Select a destination subcontractor'); return; }
    setTransferBusy(true);
    try {
      const endpoint = transferKind === 'site' ? 'transfer-site' : 'transfer-subcontractor';
      const body = transferKind === 'site'
        ? { to_project_id: transferToId, reason: transferReason }
        : { to_subcontractor_id: transferToId, reason: transferReason };
      const res = await apiPost(`/labour/${transferring.id}/${endpoint}`, body);
      toast.success(transferKind === 'site'
        ? `${transferring.name} transferred to ${res.to_project_name}`
        : `${transferring.name} transferred to ${res.to_subcontractor_name}`);
      closeTransfer();
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally { setTransferBusy(false); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.aadhar_number.replace(/\D/g, '').length !== 12) { toast.error('Valid 12-digit Aadhar required'); return; }
    if (form.mobile.replace(/\D/g, '').length < 10) { toast.error('Valid 10-digit mobile required'); return; }
    if (!form.dob) { toast.error('Date of Birth is required'); return; }
    if (!form.project_id) { toast.error('Project assignment is required'); return; }
    if (!form.subcontractor_id) { toast.error('Select an approved subcontractor (or submit a new one for approval)'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aadhar_number: form.aadhar_number.replace(/\D/g, ''),
        mobile: form.mobile.replace(/\D/g, ''),
        daily_wage: form.daily_wage ? parseFloat(form.daily_wage) : null,
      };
      const res = await apiPost('/labour-master', payload);
      const newId = res?.id || res?.labour?.id;
      toast.success(`${form.name} registered · capture face now`);
      // Mandatory face-enrol step right after creation
      const name = encodeURIComponent(form.name);
      navigate(`/m/labour/enroll?labour_id=${newId}&name=${name}&project_id=${form.project_id}&from=register`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Could not save'));
    } finally { setSaving(false); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('access_token') || '';
      const r = await axios.get(`${API}/labour-master/template.xlsx`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'labour-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Template download failed'); }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('access_token') || '';
      const r = await axios.post(`${API}/labour-master/import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.created} · skipped ${r.data.skipped}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const enrolFace = (labour, force = false) => {
    const name = encodeURIComponent(labour.name);
    const forceParam = force ? '&force=1' : '';
    navigate(`/m/labour/enroll?labour_id=${labour.id}&name=${name}&project_id=${labour.project_id || ''}&from=list${forceParam}`);
  };

  const submitNewSub = async () => {
    const nm = newSubName.trim();
    if (nm.length < 3) { toast.error('Enter the full subcontractor / firm name'); return; }
    setSubmittingSub(true);
    try {
      const created = await apiPost('/labour/subcontractors', { name: nm });
      toast.success(`'${nm}' added. Procurement will complete vendor registration before invoices can be passed.`);
      setNewSubName('');
      setShowAddSub(false);
      if (created?.id) setForm((f) => ({ ...f, subcontractor_id: created.id }));
      loadSubs();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Could not submit'));
    } finally { setSubmittingSub(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-3 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/m')} className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-slate-600" data-testid="m-labour-reg-back">
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
          <UserPlus size={15} /> Labour
        </h1>
        <div className="w-12" />
      </header>

      <div className="flex border-b border-slate-200 bg-white">
        {[
          { k: 'add', label: 'Add One' },
          { k: 'upload', label: 'Upload List' },
          { k: 'list', label: `List${labourList.length ? ` · ${labourList.length}` : ''}` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 py-3 text-xs font-display font-bold uppercase tracking-wider ${tab === t.k ? 'text-[#3b4872] border-b-2 border-[#3b4872]' : 'text-slate-500'}`}
            data-testid={`m-labour-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-3">
        {tab === 'add' && (
          <form onSubmit={submit} className="space-y-3" data-testid="m-labour-add-form">
            <MField label="Full Name *" value={form.name} onChange={upd('name')} testid="m-labour-name" />
            <MField label="Aadhar Number (12 digits) *" value={form.aadhar_number} onChange={upd('aadhar_number')} testid="m-labour-aadhar" inputMode="numeric" maxLength={12} />
            <MField label="Mobile (10 digits) *" value={form.mobile} onChange={upd('mobile')} testid="m-labour-mobile" inputMode="tel" maxLength={10} />
            <div>
              <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Date of Birth *</label>
              <input type="date" value={form.dob} onChange={upd('dob')} max={new Date().toISOString().slice(0,10)}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
                data-testid="m-labour-dob" />
            </div>
            <MSelect label="Project *" value={form.project_id} onChange={upd('project_id')} options={projects.map((p) => ({ value: p.id, label: p.name }))} testid="m-labour-project" />
            {/* Iter 209 — subcontractor must link to an APPROVED master record */}
            <div data-testid="m-labour-subcontractor-block">
              <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Subcontractor *</label>
              <select value={form.subcontractor_id} onChange={upd('subcontractor_id')}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:border-[#3b4872]"
                data-testid="m-labour-subcontractor">
                <option value="">— Select approved subcontractor —</option>
                {subcontractors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {subcontractors.length === 0 && (
                <p className="mt-1 text-[11px] text-amber-700" data-testid="m-labour-no-subs-hint">
                  No approved subcontractors yet. Add one below — it's usable immediately and Procurement will complete the vendor registration.
                </p>
              )}
              {!showAddSub ? (
                <button type="button" onClick={() => setShowAddSub(true)}
                  className="mt-1.5 text-[11px] font-display font-bold text-[#3b4872] underline"
                  data-testid="m-labour-add-sub-toggle">
                  + Not listed? Add a subcontractor
                </button>
              ) : (
                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-sm p-2.5 space-y-2" data-testid="m-labour-add-sub-panel">
                  <p className="text-[11px] text-slate-600">
                    Enter the full subcontractor / firm name. It&apos;s usable immediately — Procurement completes the vendor registration before any invoice is passed.
                  </p>
                  <input type="text" value={newSubName} onChange={(e) => setNewSubName(e.target.value)}
                    placeholder="Full subcontractor / firm name"
                    className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                    data-testid="m-labour-new-sub-name" />
                  <div className="flex gap-2">
                    <button type="button" onClick={submitNewSub} disabled={submittingSub}
                      className="flex-1 bg-[#3b4872] text-white rounded-sm px-3 py-2 text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2d3856] disabled:opacity-50 flex items-center justify-center gap-1.5"
                      data-testid="m-labour-new-sub-submit">
                      {submittingSub ? <Loader2 size={12} className="animate-spin" /> : null} Add subcontractor
                    </button>
                    <button type="button" onClick={() => { setShowAddSub(false); setNewSubName(''); }}
                      className="px-3 py-2 text-xs font-display font-bold uppercase tracking-wider text-slate-500 border border-slate-300 rounded-sm">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <MField label="Supervisor Name" value={form.supervisor_name} onChange={upd('supervisor_name')} testid="m-labour-supervisor" />
            <div className="grid grid-cols-2 gap-3">
              <MSelect label="Gender" value={form.gender} onChange={upd('gender')} options={(masters.labour_genders || []).map((v) => ({ value: v, label: v }))} testid="m-labour-gender" />
              <MSelect label="Trade" value={form.trade} onChange={upd('trade')} options={(masters.labour_trades || []).map((v) => ({ value: v, label: v }))} testid="m-labour-trade" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MSelect label="Designation" value={form.designation} onChange={upd('designation')} options={(masters.labour_designations || []).map((v) => ({ value: v, label: v }))} testid="m-labour-designation" />
              <MField label="Daily Wage (₹)" value={form.daily_wage} onChange={upd('daily_wage')} testid="m-labour-wage" inputMode="decimal" />
            </div>
            <div>
              <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Address</label>
              <textarea value={form.address} onChange={upd('address')} rows={2} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]" data-testid="m-labour-address" />
            </div>

            <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-2.5 text-[11px] text-cyan-900 flex items-start gap-2">
              <Camera size={13} className="shrink-0 mt-0.5" />
              <div>After saving, you&apos;ll be taken to the camera to capture the labour&apos;s face — this is mandatory so that the kiosk can recognise them at the gate.</div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full bg-[#3b4872] text-white rounded-sm px-4 py-3 text-sm font-display font-bold uppercase tracking-wider hover:bg-[#2d3856] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="m-labour-save">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><UserPlus size={14} /> Register & Capture Face</>}
            </button>
          </form>
        )}

        {tab === 'upload' && (
          <div className="space-y-3" data-testid="m-labour-upload-pane">
            <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
              <Upload size={32} className="mx-auto text-[#3b4872] mb-2" />
              <h3 className="font-display font-bold text-sm mb-1">Bulk Import from Excel</h3>
              <p className="text-xs text-slate-500 mb-3">Download the template, fill labour details, then upload. Existing Aadhar numbers will be skipped.</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadExcel(e.target.files?.[0])} data-testid="m-labour-file-input" />
              <div className="space-y-2">
                <button onClick={downloadTemplate} className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2" data-testid="m-labour-template-btn">
                  <Download size={13} /> Download Excel Template
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={importing}
                  className="w-full bg-[#3b4872] text-white rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2d3856] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="m-labour-upload-btn">
                  {importing ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : <><Upload size={13} /> Choose Excel File</>}
                </button>
              </div>
            </div>

            {importResult && (
              <>
                <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="m-labour-import-result">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span className="font-display font-bold text-sm">Import Result</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Stat label="Created" value={importResult.created} accent="text-emerald-700" />
                    <Stat label="Skipped" value={importResult.skipped} accent="text-amber-700" />
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-display font-bold uppercase tracking-wider text-rose-600 flex items-center gap-1 mb-1">
                        <AlertTriangle size={11} /> Errors ({importResult.errors.length})
                      </div>
                      <ul className="text-[11px] text-slate-600 space-y-1 max-h-40 overflow-y-auto">
                        {importResult.errors.slice(0, 20).map((err, i) => (
                          <li key={i} className="bg-rose-50 px-2 py-1 rounded-sm">{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {importResult.created > 0 && (
                  <button onClick={() => setTab('list')} className="w-full bg-emerald-600 text-white rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider hover:bg-emerald-700 flex items-center justify-center gap-2" data-testid="m-labour-go-list">
                    Now enrol faces for the {importResult.created} new labour → <ChevronRight size={13} />
                  </button>
                )}
              </>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[11px] text-amber-900">
              <strong>Template tips:</strong> Aadhar must be 12 digits. Mobile 10 digits. DOB in YYYY-MM-DD. Project Name must match an existing project exactly.
            </div>
          </div>
        )}

        {tab === 'list' && (
          <div className="space-y-3" data-testid="m-labour-list-pane">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="Search by name, Aadhar, mobile…"
                className="w-full bg-white border border-slate-300 rounded-sm pl-8 pr-10 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="m-labour-list-search"
              />
              <button
                onClick={() => setShowListFilters((v) => !v)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${showListFilters ? 'text-[#3b4872]' : 'text-slate-400'}`}
                data-testid="m-labour-filter-toggle"
              >
                <Filter size={14} />
              </button>
            </div>

            {/* Filters panel */}
            {showListFilters && (
              <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2" data-testid="m-labour-filters">
                <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">Filters</div>
                <select value={listFilters.project_id} onChange={(e) => setListFilters({ ...listFilters, project_id: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-xs bg-white" data-testid="m-labour-filter-project">
                  <option value="">All Projects</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={listFilters.subcontractor_id} onChange={(e) => setListFilters({ ...listFilters, subcontractor_id: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-xs bg-white" data-testid="m-labour-filter-sub">
                  <option value="">All Subcontractors</option>
                  {subcontractors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={listFilters.trade} onChange={(e) => setListFilters({ ...listFilters, trade: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-xs bg-white" data-testid="m-labour-filter-trade">
                  <option value="">All Trades</option>
                  {(masters.labour_trades || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => setListFilters({ project_id: '', subcontractor_id: '', trade: '' })} className="text-xs text-slate-500 underline">Clear filters</button>
              </div>
            )}

            {listLoading && (
              <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-[#3b4872]" /></div>
            )}
            {!listLoading && labourList.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-6 text-center text-xs text-slate-500">
                No labour registered yet. Tap <strong>Add One</strong> or <strong>Upload List</strong> to start.
              </div>
            )}
            {!listLoading && labourList.length > 0 && (() => {
              const q = listQuery.trim().toLowerCase();
              const filtered = !q ? labourList : labourList.filter((l) =>
                (l.name || '').toLowerCase().includes(q)
                || (l.aadhar_number || '').includes(q)
                || (l.mobile || '').includes(q)
                || (l.trade || '').toLowerCase().includes(q)
              );
              const notEnrolled = filtered.filter((l) => !l.face_enrolled);
              return (
                <>
                  {notEnrolled.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm p-2.5 text-[11px] text-rose-900 flex items-center gap-2">
                      <AlertTriangle size={13} className="shrink-0" />
                      <span><strong>{notEnrolled.length}</strong> labour still need face enrolment. Tap the camera button to capture.</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    {filtered.map((l) => (
                      <div key={l.id} className="bg-white border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`m-labour-row-${l.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm truncate">{l.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{l.trade || '—'} · {l.mobile || 'no mobile'}</div>
                            <div className="text-[10px] text-slate-400 font-mono">
                              {projects.find(p => p.id === l.project_id)?.name || l.project_id || '—'}
                            </div>
                          </div>
                          <button
                            onClick={() => openTransfer(l, 'site')}
                            className="text-slate-400 hover:text-indigo-600 p-1.5 rounded border border-slate-200"
                            title="Transfer to another site"
                            data-testid={`m-labour-transfer-${l.id}`}
                          >
                            <ArrowRightLeft size={14} />
                          </button>
                          <button
                            onClick={() => openTransfer(l, 'subcontractor')}
                            className="text-slate-400 hover:text-cyan-600 p-1.5 rounded border border-slate-200"
                            title="Transfer to another subcontractor"
                            data-testid={`m-labour-transfer-sub-${l.id}`}
                          >
                            <Building2 size={14} />
                          </button>
                          <button
                            onClick={() => openDocs(l)}
                            className="text-slate-400 hover:text-[#3b4872] p-1.5 rounded border border-slate-200"
                            title="Documents"
                            data-testid={`m-labour-docs-${l.id}`}
                          >
                            <FileText size={14} />
                          </button>
                        </div>
                        {/* On-site badge + IN/OUT times */}
                        <div className="flex flex-wrap items-center gap-2">
                          {l.is_onsite ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid={`m-labour-onsite-${l.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              On-site
                            </span>
                          ) : l.today_check_in ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold" data-testid={`m-labour-wasonsite-${l.id}`}>
                              Was on-site
                            </span>
                          ) : null}
                          {l.today_check_in && (
                            <span className="text-[11px] text-slate-600">
                              IN: <strong className="text-emerald-700">
                                {new Date(l.today_check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                              </strong>
                            </span>
                          )}
                          {l.today_check_out && (
                            <span className="text-[11px] text-slate-600">
                              OUT: <strong className="text-slate-700">
                                {new Date(l.today_check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                              </strong>
                            </span>
                          )}
                        </div>
                        {/* Face enrolment buttons */}
                        {l.face_enrolled ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider font-display font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-sm flex items-center gap-1" data-testid={`m-labour-enrolled-${l.id}`}>
                              <CheckCircle2 size={11} /> Enrolled
                            </span>
                            {!l.insightface_enrolled && (
                              <button
                                onClick={() => enrolFace(l)}
                                className="text-[10px] uppercase tracking-wider font-display font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 px-2.5 py-1.5 rounded-sm flex items-center gap-1"
                                data-testid={`m-labour-reenrol-${l.id}`}
                              >
                                <Zap size={11} /> Upgrade
                              </button>
                            )}
                            <button
                              onClick={() => enrolFace(l, true)}
                              className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-sm flex items-center gap-1 border border-slate-300"
                              data-testid={`m-labour-force-reenrol-${l.id}`}
                            >
                              <Camera size={11} /> Re-enrol
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => enrolFace(l)}
                            className="w-full text-[10px] uppercase tracking-wider font-display font-bold text-white bg-[#3b4872] hover:bg-[#2d3856] px-3 py-1.5 rounded-sm flex items-center justify-center gap-1"
                            data-testid={`m-labour-enrol-${l.id}`}
                          >
                            <Camera size={11} /> Enrol Face
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Transfer Modal — shared shell for Transfer Site + Transfer Subcontractor ── */}
      {transferring && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center sm:items-center p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-sm shadow-xl w-full max-w-sm p-5" data-testid={transferKind === 'site' ? 'm-transfer-site-modal' : 'm-transfer-subcontractor-modal'}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400">
                  {transferKind === 'site' ? 'Transfer Site' : 'Transfer Subcontractor'}
                </div>
                <h3 className="font-display font-bold text-base">{transferring.name}</h3>
                <p className="text-[11px] text-slate-500">
                  {transferKind === 'site' ? (
                    <>From: <b>{projects.find(p => p.id === transferring.project_id)?.name || '—'}</b></>
                  ) : (
                    <>From: <b>{transferring.subcontractor_name || '—'}</b></>
                  )}
                </p>
              </div>
              <button onClick={closeTransfer} className="text-slate-400 p-1"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                  {transferKind === 'site' ? 'Destination Site *' : 'Destination Subcontractor *'}
                </label>
                <select
                  value={transferToId}
                  onChange={(e) => setTransferToId(e.target.value)}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm bg-white"
                  data-testid="m-transfer-target-select"
                >
                  <option value="">{transferKind === 'site' ? 'Select site…' : 'Select subcontractor…'}</option>
                  {transferKind === 'site'
                    ? projectNames.filter(p => p.id !== transferring.project_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                    : subcontractors.filter(s => s.id !== transferring.subcontractor_id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm"
                  placeholder={transferKind === 'site' ? 'e.g. Phase completed' : 'e.g. Compliance handover'}
                  data-testid="m-transfer-reason"
                />
              </div>
              {transferKind === 'site' && (
                <div className="text-[11px] text-indigo-700 bg-indigo-50 rounded-sm p-2.5">
                  Face data travels with the worker — no re-enrolment needed at new site.
                </div>
              )}
            </div>
            <button
              onClick={doTransfer}
              disabled={transferBusy || !transferToId}
              className="mt-4 w-full bg-[#3b4872] text-white rounded-sm py-3 text-sm font-display font-bold uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="m-transfer-confirm"
            >
              <ArrowRightLeft size={14} /> {transferBusy ? 'Transferring…' : 'Transfer'}
            </button>
          </div>
        </div>
      )}

      {docsFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={closeDocs}>
          <div className="bg-white w-full rounded-t-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="m-labour-docs-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Documents</div>
                <h3 className="font-display font-bold text-base">{docsFor.name}</h3>
              </div>
              <button onClick={closeDocs} className="text-slate-500"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <select value={docTypeToUpload} onChange={(e) => setDocTypeToUpload(e.target.value)} className="flex-1 border border-slate-300 rounded-sm text-sm px-2 py-2.5" data-testid="m-labour-doc-type-select">
                  {LABOUR_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm" data-testid="m-labour-doc-upload-btn">
                  <Upload size={13} /> {docUploading ? '…' : 'Upload'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={docUploading}
                    onChange={(e) => { if (e.target.files[0]) uploadDoc(e.target.files[0], docTypeToUpload); e.target.value = ''; }} />
                </label>
              </div>
              {docsLoading ? (
                <div className="text-center py-6 text-sm text-slate-400">Loading…</div>
              ) : docs.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400" data-testid="m-labour-docs-empty">No documents uploaded yet.</div>
              ) : docs.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-sm p-2.5" data-testid={`m-labour-doc-row-${d.id}`}>
                  <div className="min-w-0">
                    <a href={d.url} target="_blank" rel="noreferrer" className="font-display font-semibold text-sm text-[#3b4872]">{d.doc_type}</a>
                    <div className="text-[10px] text-slate-500 truncate">{d.filename}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {d.verified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold"><CheckCircle2 size={11} /> Verified</span>
                    ) : (
                      <button onClick={() => verifyDoc(d.id)} className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300 text-amber-700 font-bold" data-testid={`m-labour-doc-verify-${d.id}`}>Verify</button>
                    )}
                    <label className="text-slate-500">
                      <Upload size={14} />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" disabled={docUploading}
                        onChange={(e) => { if (e.target.files[0]) uploadDoc(e.target.files[0], d.doc_type, d.id); e.target.value = ''; }} />
                    </label>
                    <button onClick={() => deleteDoc(d.id)} className="text-rose-600" data-testid={`m-labour-doc-delete-${d.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MField({ label, value, onChange, testid, inputMode, maxLength }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <input type="text" value={value || ''} onChange={onChange} inputMode={inputMode} maxLength={maxLength}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#3b4872]"
        data-testid={testid} />
    </div>
  );
}
function MSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <select value={value || ''} onChange={onChange}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:border-[#3b4872]"
        data-testid={testid}>
        <option value="">— Select —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="bg-slate-50 rounded-sm px-3 py-2">
      <div className="text-[9px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-display font-bold ${accent}`}>{value}</div>
    </div>
  );
}
